import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState, createContext, useContext } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/App";
import { useInstallPrompt } from "@/lib/theme";
import { LayoutDashboard, Calendar, Trophy, Settings as SettingsIcon, CreditCard, LogOut, Users, Menu, Wallet, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PublicLinkButton } from "@/components/PublicLink";
import { Lock, Clock } from "lucide-react";

const OrgContext = createContext(null);
export const useOrg = () => useContext(OrgContext);

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canInstall, installed, install } = useInstallPrompt();

  const reloadOrg = async () => {
    try {
      const { data } = await api.get("/orgs/me");
      if (!data) {
        navigate("/onboarding", { replace: true });
        return;
      }
      setOrg(data);
    } catch {
      navigate("/onboarding", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadOrg(); }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-emerald-500 font-heading text-2xl">CARGANDO...</div>;

  const expired = org?.plan_status === "expired";
  if (expired && location.pathname !== "/dashboard/subscription") {
    return <Navigate to="/dashboard/subscription" replace />;
  }

  const sections = [
    {
      title: "General",
      items: [
        { to: "/dashboard", icon: LayoutDashboard, label: "Overview", exact: true },
      ],
    },
    {
      title: "Turnos",
      items: [
        { to: "/dashboard/bookings", icon: Calendar, label: "Agenda" },
        { to: "/dashboard/courts", icon: Trophy, label: "Canchas" },
        { to: "/dashboard/customers", icon: Users, label: "Clientes" },
      ],
    },
    {
      title: "Ganancias",
      items: [
        { to: "/dashboard/earnings", icon: Wallet, label: "Ingresos" },
        { to: "/dashboard/subscription", icon: CreditCard, label: "Plan" },
      ],
    },
    {
      title: "Cuenta",
      items: [
        { to: "/dashboard/settings", icon: SettingsIcon, label: "Ajustes" },
      ],
    },
  ];

  const trialEnd = org && new Date(org.trial_end);
  const daysLeft = org ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;

  const SidebarContent = ({ onNav }) => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-[#262626]">
        <div className="font-heading text-2xl tracking-widest text-emerald-400">MESSIRVE</div>
        <div className="text-xs text-zinc-500 mt-1 truncate" data-testid="sidebar-org-name">{org?.name}</div>
      </div>
      <nav className="flex-1 p-4 space-y-5 overflow-y-auto">
        {sections.map((sec) => (
          <div key={sec.title}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-bold px-2 mb-2">{sec.title}</div>
            <div className="space-y-1">
              {sec.items.map((it) => {
                const Icon = it.icon;
                if (expired && it.to !== "/dashboard/subscription") {
                  return (
                    <div key={it.to} className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm uppercase tracking-widest text-zinc-700 cursor-not-allowed" data-testid={`nav-${it.label.toLowerCase()}-locked`}>
                      <Lock className="h-4 w-4" strokeWidth={1.5} /> {it.label}
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.exact}
                    onClick={onNav}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm uppercase tracking-widest transition ${
                        isActive ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "text-zinc-400 hover:bg-[#141414] hover:text-white"
                      }`
                    }
                    data-testid={`nav-${it.label.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} /> {it.label}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
        {canInstall && !installed && (
          <button
            onClick={() => { install(); onNav && onNav(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm uppercase tracking-widest text-black bg-emerald-500 hover:bg-emerald-600 font-bold"
            data-testid="sidebar-install-btn"
          >
            <Download className="h-4 w-4" strokeWidth={2} /> Instalar app
          </button>
        )}
      </nav>
      <div className="p-4 border-t border-[#262626]">
        {org?.plan_status === "trial" && (
          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded mb-3 text-xs" data-testid="trial-banner">
            <div className="uppercase tracking-widest text-amber-400 font-bold mb-1">Prueba gratuita</div>
            <div className="text-zinc-400">{daysLeft} {daysLeft === 1 ? "día restante" : "días restantes"}</div>
          </div>
        )}
        {expired && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded mb-3 text-xs" data-testid="expired-sidebar-banner">
            <div className="uppercase tracking-widest text-red-400 font-bold mb-1">Prueba vencida</div>
            <div className="text-zinc-400">Activá el plan para continuar</div>
          </div>
        )}
        {org?.plan_status === "active" && (
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded mb-3 text-xs">
            <div className="uppercase tracking-widest text-emerald-400 font-bold">Plan activo</div>
          </div>
        )}
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold">
              {user?.name?.[0] || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={logout} className="w-full border-zinc-700 bg-transparent text-zinc-400 hover:bg-[#141414] uppercase tracking-widest text-xs" data-testid="logout-btn">
          <LogOut className="h-3 w-3 mr-2" /> Salir
        </Button>
      </div>
    </div>
  );

  return (
    <OrgContext.Provider value={{ org, reloadOrg }}>
      <div className="min-h-screen bg-[#0A0A0A] text-white md:flex">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-[#262626] flex items-center justify-between px-4 h-14">
          <div className="font-heading text-xl tracking-widest text-emerald-400">MESSIRVE</div>
          <div className="flex items-center gap-2">
            {!expired && <PublicLinkButton org={org} compact />}
            {canInstall && !installed && (
              <Button
                onClick={install}
                size="sm"
                className="h-9 bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest text-xs font-bold px-3"
                data-testid="topbar-install-btn"
              >
                <Download className="h-3 w-3 mr-1" /> Instalar
              </Button>
            )}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#0A0A0A] border-r border-[#262626] text-white">
                <SidebarContent onNav={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
        </header>

        {/* Desktop sidebar */}
        <aside className="hidden md:flex w-64 border-r border-[#262626] bg-[#0A0A0A] flex-col sticky top-0 h-screen">
          <SidebarContent />
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          {org?.plan_status === "trial" && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 md:px-10 py-3 bg-amber-500/10 border-b border-amber-500/30" data-testid="trial-countdown-bar">
              <div className="flex items-center gap-2 text-sm text-amber-300">
                <Clock className="h-4 w-4" />
                <span>Te quedan <b data-testid="trial-days-left">{daysLeft}</b> {daysLeft === 1 ? "día" : "días"} de prueba</span>
              </div>
              <Button size="sm" onClick={() => navigate("/dashboard/subscription")} className="h-8 bg-amber-400 hover:bg-amber-500 text-black uppercase tracking-widest text-xs font-bold" data-testid="trial-pay-btn">
                Pagar plan
              </Button>
            </div>
          )}
          {!expired && (
            <div className="hidden md:flex justify-end px-10 lg:px-12 pt-6">
              <PublicLinkButton org={org} />
            </div>
          )}
          <main className={`flex-1 p-4 sm:p-6 md:p-10 lg:p-12 overflow-x-hidden ${expired ? "" : "md:pt-4 lg:pt-4"}`}>
            <Outlet />
          </main>
        </div>
      </div>
    </OrgContext.Provider>
  );
}
